import { scheduleRoadmap } from './utils/roadmapSchedule';
import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import RoadmapDisplay from './RoadmapDisplay';
import ProgressTracker from './ProgressTracker';
import { getFeatureFlag } from './featureFlags';
import { ChevronDown } from 'lucide-react';
import RoadmapGate from './components/RoadmapGate';

const validationSchema = Yup.object().shape({
  hoursPerWeek: Yup.number().required('Required').positive('Must be positive').integer('Must be an integer'),
  experienceLevel: Yup.string().required('Required'),
  codingLevel: Yup.string().required('Required'),
  budget: Yup.string().required('Required'),
  learningStyle: Yup.string().required('Required'),
  salaryGoal6Months: Yup.number().required('Required').positive('Must be positive').integer('Must be an integer'),
  salaryGoal18Months: Yup.number().required('Required').positive('Must be positive').integer('Must be an integer'),
  workingFullTime: Yup.string().required('Required'),
});

function Questionnaire() {
  const [roadmap, setRoadmap] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showScrollArrow, setShowScrollArrow] = useState(false);

  const initialValues = {
    hoursPerWeek: '',
    experienceLevel: '',
    codingLevel: '',
    budget: '',
    learningStyle: '',
    salaryGoal6Months: '',
    salaryGoal18Months: '',
    workingFullTime: '',
  };

  const certificationDetails = {
    "CompTIA Security+": { baseDuration: 7, cost: 240 },
    "AWS Cloud Practitioner": { baseDuration: 2, cost: 100 },
    "Terraform Associate": { baseDuration: 3, cost: 70 },
    "GitHub Actions": { baseDuration: 3, cost: 95 },
    "Kubernetes and Cloud Native Security Associate": { baseDuration: 2, cost: 300 },
  };

  const generateRoadmap = (values) => {
    let roadmapItems = [];
    let certifications = [];
    let timelineMessage = '';
    let messageColor = '';
    const {
      hoursPerWeek, budget, experienceLevel, learningStyle, codingLevel,
      salaryGoal6Months, salaryGoal18Months, workingFullTime
    } = values;

    // Adjust timeline based on hours per week
    let timeMultiplier = 1;
    let totalMonths = 6;
    let weekAdjustment = 0;

    if (hoursPerWeek >= 0 && hoursPerWeek <= 10) {
      timeMultiplier = 1.5;
      totalMonths = 7;
      weekAdjustment = 1;
      timelineMessage = "Your timeline will be delayed.";
      messageColor = "text-red-500";
    } else if (hoursPerWeek >= 11 && hoursPerWeek <= 20) {
      timeMultiplier = 1;
    } else if (hoursPerWeek >= 21) {
      timeMultiplier = 0.75;
      weekAdjustment = -1;
      timelineMessage = "You will be on course to meet the 6-month objective.";
      messageColor = "text-green-400";
    }

    const addRoadmapItem = (title, durationWeeks, description, startMonth, endMonth, coveredInBootcamp = true) => {
      const adjustedDuration = Math.max(1, Math.ceil(durationWeeks * timeMultiplier) + weekAdjustment);
      roadmapItems.push({
        title: title.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        duration: adjustedDuration,
        startMonth,
        endMonth,
        description: description.charAt(0).toUpperCase() + description.slice(1),
        type: 'skill',
        coveredInBootcamp: coveredInBootcamp
      });
    };
    
    const addCertification = (name, durationWeeks, cost, startMonth, endMonth, coveredInBootcamp = false) => {
      const adjustedDuration = Math.max(1, Math.ceil(durationWeeks * timeMultiplier) + weekAdjustment);
      certifications.push({
        name: name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        duration: adjustedDuration,
        startMonth,
        endMonth,
        cost,
        type: 'certification',
        coveredInBootcamp: coveredInBootcamp
      });
    };

    // Core skills (these are always added regardless of budget)
    if (codingLevel === 'None' || codingLevel === 'Basic') {
      addRoadmapItem("Git, GitHub and Visual Studio Code", 1, "Learn version control and IDE basics", 1, 1);
      addRoadmapItem("Command Line and Terminal", 1, "Master command line interfaces", 1, 1);
    }
    addRoadmapItem("Security Fundamentals", 2, "Learn basic security concepts and principles", 1, 1);
    addRoadmapItem("Software Engineering Fundamentals", 2, "Learn basic software engineering principles", 1, 1);
    addRoadmapItem("Python Code Academy", 1, "Learn Python programming basics", 2, 2, false);
    addRoadmapItem("DevSecOps Fundamentals", 2, "Learn DevSecOps principles including Threat Modelling, SAST, IaC Scanning, DAST, SCA scanning, Security Architecture", 3, 3);
    addRoadmapItem("Interview Prep and CV", 1, "Prepare for job interviews and update CV", 6, 6);

    // Certifications with updated months
    addCertification("CompTIA Security+", 7, 240, 2, 3, true);
    addCertification("AWS Cloud Practitioner", 2, 100, 3, 4, true);
    addCertification("Terraform Associate", 3, 70, 5, 6, true);
    addCertification("GitHub Actions", 3, 95, 6, 6, true);
    addCertification("Kubernetes and Cloud Native Security Associate", 2, 300, 4, 5, true);

    // Budget-based certification selection
    let totalCertificationCost = 0;
    let budgetLimit = parseInt(budget);

    if (budget === "2500") {
      budgetLimit = Infinity; // No budget limit for "£2,000+"
    }

    const addCertificationIfWithinBudget = (cert) => {
      if (totalCertificationCost + cert.cost <= budgetLimit) {
        totalCertificationCost += cert.cost;
        return true;
      }
      return false;
    };

    // Sort certifications by cost (ascending) to prioritize cheaper ones
    const sortedCertifications = Object.entries(certificationDetails)
      .sort(([, a], [, b]) => a.cost - b.cost)
      .map(([name, details], index) => ({ name, ...details, startMonth: index + 2 }));

    certifications = certifications.filter(cert => addCertificationIfWithinBudget(cert));

    // Add message about certifications based on budget
    if (budgetLimit === Infinity) {
      timelineMessage += " All certifications included based on your budget.";
    } else if (totalCertificationCost < budgetLimit) {
      timelineMessage += ` Certifications within your budget of £${budgetLimit} included.`;
    } else {
      timelineMessage += ` Some certifications omitted due to budget limit of £${budgetLimit}.`;
    }

    // Adjust roadmap based on experience level
    if (experienceLevel === 'Advanced') {
      timelineMessage += " Advanced track: Some basic items removed.";
      messageColor = "text-green-400";
    }

    // Adjust for coding level
    if (codingLevel === 'Intermediate' || codingLevel === 'Advanced') {
      timelineMessage += " Some basic development tools omitted due to your coding level.";
      messageColor = "text-green-400";
    }

    // Adjust for full-time work
    if (workingFullTime === 'yes') {
      timelineMessage += " As you have indicated you're in a full-time job, we recommend you utilise evening training, podcasts and apps so you can learn on the move and in your spare time.";
    } else {
      timelineMessage += " As you're not in full-time work we recommend you look to complete 20-40hrs per week on up-skilling.";
    }

    // Adjust roadmap based on 6-month salary goal
    const salary6Months = parseInt(salaryGoal6Months);

    if (salary6Months <= 60000) {
      // Keep normal roadmap timeline
      timelineMessage += " Your 6-month salary goal aligns with the standard roadmap.";
    } else if (salary6Months <= 90000) {
      totalMonths = 7;
      addRoadmapItem(
        "Tech Work Experience", 
        4, 
        "You will require a job role with experience to hit the 6-month salary target you have requested.", 
        7,
        7,
        false
      );
      timelineMessage += " To achieve your 6-month salary goal, you'll need some work experience after completing the initial roadmap.";
      messageColor = "text-yellow-500";
    } else {
      totalMonths = 7;
      addRoadmapItem(
        "Extensive Tech Work Experience", 
        4, 
        "You will require multiple previous job roles with experience to hit the 6-month salary target you have requested.", 
        7,
        7,
        false
      );
      timelineMessage += " Your ambitious 6-month salary goal requires significant prior work experience in addition to completing this roadmap.";
      messageColor = "text-orange-500";
    }

    // Adjust roadmap based on 18-month salary goal
    const salary18Months = parseInt(salaryGoal18Months);

    if (salary18Months <= 60000) {
      timelineMessage += " Your 18-month salary goal aligns with the standard roadmap timeline.";
    } else if (salary18Months <= 90000) {
      if (totalMonths < 7) {
        totalMonths = 7;
        addRoadmapItem(
          "Tech Work Experience", 
          4, 
          "You will require moving from your first cyber job role with leverage to hit the 18-month salary target you have requested in your second role by 18 months.", 
          7,
          7,
          false
        );
      }
      timelineMessage += " To achieve your 18-month salary goal, you'll need some work experience after completing the initial roadmap.";
      messageColor = messageColor || "text-yellow-500";
    } else {
      if (totalMonths < 7) {
        totalMonths = 7;
        addRoadmapItem(
          "Extensive Tech Work Experience", 
          4, 
          "You will require multiple previous job roles with experience to hit the 18-month salary target you have requested.", 
          7,
          7,
          false
        );
      }
      timelineMessage += " Your ambitious 18-month salary goal requires significant prior work experience in addition to completing this roadmap.";
      messageColor = messageColor || "text-orange-500";
    }

    setRoadmap(scheduleRoadmap({
      items: [...roadmapItems, ...certifications], timelineMessage, messageColor, totalMonths
    }));
    console.log('Roadmap generated, showing scroll arrow');
    setShowScrollArrow(true);
  };

  useEffect(() => {
    if (showScrollArrow) {
      console.log('Scroll arrow shown');
      const timer = setTimeout(() => {
        console.log('Hiding scroll arrow');
        setShowScrollArrow(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showScrollArrow]);

  const scrollToRoadmap = () => {
    console.log('Attempting to scroll to roadmap');
    const roadmapElement = document.getElementById('roadmap-section');
    if (roadmapElement) {
      console.log('Roadmap element found, scrolling');
      roadmapElement.scrollIntoView({ behavior: 'smooth' });
    } else {
      console.log('Roadmap element not found');
    }
  };

  return (
    <main className="flex-1 overflow-x-hidden overflow-y-auto bg-slate-900 text-white py-6 relative">
      <div className="container mx-auto px-6">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-2 text-green-400">Tekyu</h1>
          <h2 className="text-3xl font-bold mb-2 text-white">DevSecOps Roadmap Calculator</h2>
          <p className="text-xl text-slate-300">Elevate Your Career in DevSecOps</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              generateRoadmap(values);
              setSubmitting(false);
            }}
          >
            {({ values, isSubmitting }) => (
              <Form className="rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 p-8 mb-8 border border-slate-700 shadow-xl">
                <div className="mb-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-400/20 mb-4">
                    <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-3">Career Roadmap Generator</h2>
                  <p className="text-slate-300 text-lg">
                    Get a personalized DevSecOps & AI Security career roadmap tailored to your goals
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="hoursPerWeek" className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Hours Per Week *
                    </label>
                    <Field
                      type="number"
                      name="hoursPerWeek"
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 text-white px-4 py-3 focus:border-green-400 focus:outline-none transition-colors"
                      placeholder="e.g., 20"
                    />
                    <ErrorMessage name="hoursPerWeek" component="div" className="text-red-400 text-sm mt-1" />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="experienceLevel" className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Experience Level *
                    </label>
                    <Field
                      as="select"
                      name="experienceLevel"
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 text-white px-4 py-3 focus:border-green-400 focus:outline-none transition-colors"
                    >
                      <option value="">Select Experience Level</option>
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </Field>
                    <ErrorMessage name="experienceLevel" component="div" className="text-red-400 text-sm mt-1" />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="codingLevel" className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                      Coding Level *
                    </label>
                    <Field
                      as="select"
                      name="codingLevel"
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 text-white px-4 py-3 focus:border-green-400 focus:outline-none transition-colors"
                    >
                      <option value="">Select Coding Level</option>
                      <option value="None">None</option>
                      <option value="Basic">Basic</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </Field>
                    <ErrorMessage name="codingLevel" component="div" className="text-red-400 text-sm mt-1" />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="budget" className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      External study budget (£) *
                    </label>
                    <Field
                      as="select"
                      name="budget"
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 text-white px-4 py-3 focus:border-green-400 focus:outline-none transition-colors"
                    >
                      <option value="">Select Budget</option>
                      <option value="0">£0 — free learning resources</option>
                      <option value="500">£500</option>
                      <option value="1000">£1,000</option>
                      <option value="1500">£1,500</option>
                      <option value="2000">£2,000</option>
                      <option value="2500">£2,000+</option>
                    </Field>
                    <ErrorMessage name="budget" component="div" className="text-red-400 text-sm mt-1" />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="learningStyle" className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      Learning Style *
                    </label>
                    <Field
                      as="select"
                      name="learningStyle"
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 text-white px-4 py-3 focus:border-green-400 focus:outline-none transition-colors"
                    >
                      <option value="">Select Learning Style</option>
                      <option value="self-paced">Self-Paced</option>
                      <option value="structured">Structured</option>
                    </Field>
                    <ErrorMessage name="learningStyle" component="div" className="text-red-400 text-sm mt-1" />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="salaryGoal6Months" className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      Salary Goal (6 Months) *
                    </label>
                    <Field
                      type="number"
                      name="salaryGoal6Months"
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 text-white px-4 py-3 focus:border-green-400 focus:outline-none transition-colors"
                      placeholder="e.g., 50000"
                    />
                    <ErrorMessage name="salaryGoal6Months" component="div" className="text-red-400 text-sm mt-1" />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="salaryGoal18Months" className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      Salary Goal (18 Months) *
                    </label>
                    <Field
                      type="number"
                      name="salaryGoal18Months"
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 text-white px-4 py-3 focus:border-green-400 focus:outline-none transition-colors"
                      placeholder="e.g., 70000"
                    />
                    <ErrorMessage name="salaryGoal18Months" component="div" className="text-red-400 text-sm mt-1" />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="workingFullTime" className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Working Full-Time? *
                    </label>
                    <Field
                      as="select"
                      name="workingFullTime"
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 text-white px-4 py-3 focus:border-green-400 focus:outline-none transition-colors"
                    >
                      <option value="">Select Option</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </Field>
                    <ErrorMessage name="workingFullTime" component="div" className="text-red-400 text-sm mt-1" />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-8 w-full rounded-lg bg-green-400 px-6 py-4 text-white font-bold text-lg hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2 focus:ring-offset-slate-900 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Generate My Roadmap
                </button>
              </Form>
            )}
          </Formik>
          
          {roadmap && (
            <div id="roadmap-section">
              <RoadmapGate roadmap={roadmap} roleKey="devsecops">
                {({ isUnlocked, maxMonth }) => (
                  <>
                    {roadmap.timelineMessage && (
                      <p className={`text-center mb-4 font-bold ${roadmap.messageColor}`}>
                        {roadmap.timelineMessage}
                      </p>
                    )}
                    <RoadmapDisplay roadmap={roadmap} isUnlocked={isUnlocked} maxMonth={maxMonth} />
                  </>
                )}
              </RoadmapGate>
            </div>
          )}
          
          {getFeatureFlag('showProgressTracker') && (
            <div className="bg-slate-800 shadow-lg rounded-lg p-8 border border-green-400 mt-8">
              <ProgressTracker 
                roadmap={roadmap} 
                isSubscribed={isSubscribed} 
                setIsSubscribed={setIsSubscribed}
              />
            </div>
          )}
        </div>
      </div>

      {showScrollArrow && (
        <button 
          onClick={() => {
            console.log('Scroll arrow clicked');
            scrollToRoadmap();
          }}
          className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-green-400 text-white p-4 rounded-full shadow-lg animate-bounce z-50"
          style={{ width: '60px', height: '60px' }}
          aria-label="Scroll to roadmap"
        >
          <ChevronDown size={36} />
        </button>
      )}
    </main>
  );
}

export default Questionnaire;